import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import AiPromptGenerator from "@/components/resume-builder/AiPromptGenerator";

const FIELD_CLASS = "bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400";

const EMPTY_ENTRY = { title: "", company: "", location: "", start_date: "", end_date: "", current: false, bullets: [] };

function ExperienceEntryCard({ entry, idx, onChange, onRemove }) {
  const [suggesting, setSuggesting] = useState(false);

  const update = (field, value) => onChange(idx, { ...entry, [field]: value });

  const suggestBullets = async () => {
    if (!entry.title.trim()) {
      toast.error("Add a job title first");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post("/resume-builder/suggest-bullets", { job_title: entry.title, company: entry.company });
      update("bullets", [...entry.bullets, ...data.bullets]);
    } catch (e) {
      toast.error("Could not suggest bullets right now");
    } finally {
      setSuggesting(false);
    }
  };

  const updateBullet = (i, value) => update("bullets", entry.bullets.map((b, bi) => (bi === i ? value : b)));
  const removeBullet = (i) => update("bullets", entry.bullets.filter((_, bi) => bi !== i));

  return (
    <div className="bg-white/[0.03] rounded-xl p-4" data-testid={`resume-experience-entry-${idx}`}>
      <div className="flex justify-end mb-2">
        <button onClick={() => onRemove(idx)} data-testid={`resume-remove-experience-${idx}`} className="text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <input value={entry.title} onChange={(e) => update("title", e.target.value)} placeholder="Job Title" data-testid={`resume-experience-title-${idx}`} className={FIELD_CLASS} />
        <input value={entry.company} onChange={(e) => update("company", e.target.value)} placeholder="Company" data-testid={`resume-experience-company-${idx}`} className={FIELD_CLASS} />
        <input value={entry.location} onChange={(e) => update("location", e.target.value)} placeholder="Location" data-testid={`resume-experience-location-${idx}`} className={FIELD_CLASS} />
        <div className="flex gap-2">
          <input value={entry.start_date} onChange={(e) => update("start_date", e.target.value)} placeholder="Start (e.g. Jan 2023)" data-testid={`resume-experience-start-${idx}`} className={FIELD_CLASS + " flex-1"} />
          <input value={entry.end_date} onChange={(e) => update("end_date", e.target.value)} placeholder="End" disabled={entry.current} data-testid={`resume-experience-end-${idx}`} className={FIELD_CLASS + " flex-1 disabled:opacity-50"} />
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-400 mb-3">
        <input type="checkbox" checked={entry.current} onChange={(e) => update("current", e.target.checked)} data-testid={`resume-experience-current-${idx}`} /> I currently work here
      </label>

      <div className="space-y-1.5 mb-2">
        {entry.bullets.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={b} onChange={(e) => updateBullet(i, e.target.value)} data-testid={`resume-experience-bullet-${idx}-${i}`} className={FIELD_CLASS + " flex-1"} />
            <button onClick={() => removeBullet(i)} data-testid={`resume-remove-bullet-${idx}-${i}`} className="text-slate-400 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <button onClick={suggestBullets} disabled={suggesting} data-testid={`resume-suggest-bullets-${idx}`} className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200 disabled:opacity-60">
        {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Suggest Bullets
      </button>
    </div>
  );
}

export default function ExperienceSection({ draft, setDraft }) {
  const updateEntry = (idx, entry) => setDraft({ ...draft, experience: draft.experience.map((e, i) => (i === idx ? entry : e)) });
  const removeEntry = (idx) => setDraft({ ...draft, experience: draft.experience.filter((_, i) => i !== idx) });
  const addEntry = () => setDraft({ ...draft, experience: [...draft.experience, { ...EMPTY_ENTRY }] });

  return (
    <div className="glass-panel rounded-2xl p-5" data-testid="resume-section-experience">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-white">Experience</p>
        <button onClick={addEntry} data-testid="resume-add-experience-button" className="flex items-center gap-1 text-xs font-semibold text-indigo-300 hover:text-indigo-200">
          <Plus className="w-3.5 h-3.5" /> Add Experience
        </button>
      </div>
      <div className="space-y-3">
        {draft.experience.map((entry, idx) => (
          <ExperienceEntryCard key={idx} entry={entry} idx={idx} onChange={updateEntry} onRemove={removeEntry} />
        ))}
        {draft.experience.length === 0 && <p className="text-xs text-slate-400">No experience added yet.</p>}
      </div>
      <div className="mt-3">
        <AiPromptGenerator section="experience" draft={draft} setDraft={setDraft} />
      </div>
    </div>
  );
}
